/**
 * FollowCompanyButton (Iter321).
 *
 * Drop-in button that adds/removes a company from the current user's
 * watchlist. Idempotent — the backend PATCHes the row when it already
 * exists, so double-clicks never duplicate.
 *
 * Consumers pass the minimum company shape (name + optional city/state/
 * segment/company_id). Any missing field is fine — the backend fills in
 * defaults.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookmarkPlus, BookmarkCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FollowCompanyButton({
  company,           // { company_id?, company_name, city, state, segment, website }
  size = "sm",       // "sm" | "md"
  testid,
  onChange,          // optional callback(isFollowed)
}) {
  const [busy, setBusy] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [watchId, setWatchId] = useState(null);

  // Cheap check on mount — hits /watchlist and looks for the same company.
  // The endpoint is fast (single tenant scope + Mongo index on user_email).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/smith/watchlist?limit=500`);
        if (cancelled) return;
        const match = (data.items || []).find((w) =>
          (company.company_id && w.company_id === company.company_id) ||
          (w.company_name || "").toLowerCase() === (company.company_name || "").toLowerCase()
        );
        if (match) { setFollowed(true); setWatchId(match.id); }
      } catch (_e) { /* silent — hides button state, doesn't break page */ }
    })();
    return () => { cancelled = true; };
  }, [company.company_id, company.company_name]);

  const follow = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/smith/watchlist`, {
        company_id: company.company_id || null,
        company_name: company.company_name,
        city: company.city || null,
        state: company.state || null,
        segment: company.segment || null,
        website: company.website || null,
      });
      setFollowed(true);
      setWatchId(data.item.id);
      toast.success(data.created ? `Now following ${company.company_name}` : "Already in your watchlist");
      onChange?.(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Follow failed");
    } finally { setBusy(false); }
  };

  const unfollow = async () => {
    if (!watchId) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/smith/watchlist/${watchId}`);
      setFollowed(false);
      setWatchId(null);
      toast.success(`Unfollowed ${company.company_name}`);
      onChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unfollow failed");
    } finally { setBusy(false); }
  };

  const h = size === "md" ? "h-8" : "h-7";
  const pad = size === "md" ? "px-3" : "px-2.5";
  const tx = size === "md" ? "text-[12px]" : "text-[11px]";
  const iconSize = size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";

  if (followed) {
    return (
      <button type="button" onClick={unfollow} disabled={busy}
              data-testid={testid || "follow-company-btn"}
              title="Unfollow — stop weekly re-scans"
              className={`${h} ${pad} rounded ${tx} font-bold text-emerald-200 border border-emerald-400/40 bg-emerald-500/15 hover:bg-emerald-500/25 inline-flex items-center gap-1.5 disabled:opacity-50`}>
        {busy ? <Loader2 className={`${iconSize} animate-spin`} /> : <BookmarkCheck className={iconSize} />}
        Following
      </button>
    );
  }
  return (
    <button type="button" onClick={follow} disabled={busy}
            data-testid={testid || "follow-company-btn"}
            title="Follow — weekly automatic re-scan for expansion, hiring, contracts and news"
            className={`${h} ${pad} rounded ${tx} font-bold text-slate-200 border border-white/15 hover:border-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1.5 disabled:opacity-50`}>
      {busy ? <Loader2 className={`${iconSize} animate-spin`} /> : <BookmarkPlus className={iconSize} />}
      Follow
    </button>
  );
}
